// libraries
import Vue from 'vue';
import VueI18n from 'vue-i18n';
import VueDraggable from 'vuedraggable';
import moment from 'moment';
import Axios from 'axios';
import DOMPurify from 'dompurify';
import 'element-scroll-polyfill';
// config
import AppConfig from './env.js';
// core
import App from './components/views/App';
import router from './router';
import store from './store/index';
// common
import Btn from './components/common/Btn';
import BadgeFirstVisit from './components/common/BadgeFirstVisit';
import BadgeInvoiceRequest from './components/common/BadgeInvoiceRequest';
import Counter from './components/common/Counter';
import Field from './components/common/Field';
import Icon from './components/common/Icon';
import InfoBox from './components/common/InfoBox';
import LanguageSwitcher from './components/common/LanguageSwitcher';
import Switcher from './components/common/Switcher';
import Message from './components/common/Message';
import SanitizeUtils from './components/helpers/SanitizeUtils';
import Translations from './langs/loader.js';
// plugins
import FaviconPlugin from './plugins/favicon';

// DOMPurify configuration
DOMPurify.addHook('afterSanitizeAttributes', function (node) {
  // set all elements owning target to target=_blank
  if ('target' in node) {
    if (!(node.getAttribute('href') && node.getAttribute('href').indexOf('/') === 0)) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  }

  // set non-HTML/MathML links to xlink:show=new
  if (!node.hasAttribute('target') && (node.hasAttribute('xlink:href') || node.hasAttribute('href'))) {
    node.setAttribute('xlink:show', 'new');
  }
});

// Get app language
let appLanguage;

if (navigator.language === 'pl' || navigator.language === 'pl-PL') {
  appLanguage = 'pl';
} else {
  appLanguage = 'en';
}

// Additional configuration
Vue.config.productionTip = false;

// Add global Vue properties for commonly used libraries and variables
Vue.prototype.$moment = moment;
Vue.prototype.$moment.locale(appLanguage);
Vue.prototype.$window = window;
Vue.prototype.$allowedImageFiles = '.jpg,.jpeg,.png,.webp';

// Add global Vue property with local app configuration
Vue.prototype.$config = AppConfig;
Vue.prototype.$http = Axios;
Vue.prototype.$http.defaults.baseURL = AppConfig.apiServerUrl;

if (SanitizeUtils.getToken()) {
  Vue.prototype.$http.defaults.headers['Authorization'] = 'Bearer ' + SanitizeUtils.getToken();
  Vue.prototype.$http.defaults.headers['Cache-Control'] = 'no-store,max-age=0';
}

// Add use of additional libraries
Vue.use(VueI18n);

// Add use of additional plugins
Vue.use(FaviconPlugin);

// Declare event bus
Vue.prototype.$bus = new Vue();

Vue.prototype.$http.interceptors.response.use(
  response => {
    Vue.prototype.$bus.$emit('bookero-network-online');
    return response;
  },
  error => {
    if (!error.response) {
      Vue.prototype.$bus.$emit('bookero-network-offline');
    } else {
      Vue.prototype.$bus.$emit('bookero-network-online');
    }
    return Promise.reject(error);
  }
);

// Register global components
Vue.component('btn', Btn);
Vue.component('counter', Counter);
Vue.component('field', Field);
Vue.component('badge-first-visit', BadgeFirstVisit);
Vue.component('badge-invoice-request', BadgeInvoiceRequest);
Vue.component('icon', Icon);
Vue.component('info-box', InfoBox);
Vue.component('language-switcher', LanguageSwitcher);
Vue.component('message', Message);
Vue.component('switcher', Switcher);
Vue.component('draggable', VueDraggable);

const DOMPurifyConfig = {
  ADD_TAGS: ['iframe'],
  ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling']
};

// Register v-pure-html directive
Vue.directive('pure-html', {
  inserted: function (el, binding) {
    if (binding.oldValue === binding.value) {
      return;
    }
    el.innerHTML = DOMPurify.sanitize(binding.value, DOMPurifyConfig).replace(/<iframe/gmi, '<bookero-iframe').replace(/<\/iframe/gmi, '</bookero-iframe');
  },
  update: function (el, binding) {
    if (binding.oldValue === binding.value) {
      return;
    }
    el.innerHTML = DOMPurify.sanitize(binding.value, DOMPurifyConfig).replace(/<iframe/gmi, '<bookero-iframe').replace(/<\/iframe/gmi, '</bookero-iframe');
  }
});

// Configure languages
const i18n = new VueI18n({
  locale: appLanguage,
  messages: Translations,
  pluralizationRules: {
    'pl': function (choice) {
      if (choice === 0) {
        return 0;
      }

      if (choice === 1) {
        return 1;
      }

      if (choice % 100 > 10 && choice % 100 < 20) {
        return 3;
      }

      if (choice % 10 >= 2 && choice % 10 <= 4) {
        return 2;
      }

      return 3;
    }
  }
});

/* eslint-disable no-new */
new Vue({
  el: '#app',
  i18n,
  router,
  store,
  render: h => h(App),
  components: {
    'App': App
  },
  data () {
    return {
      defaultCurrency: '',
      renewCurrency: '',
      formReferrer: '',
      appLanguage
    };
  },
  mounted () {
    document.title = this.$t('app.pageTitle');
    // Security notice for users
    console.log('%c⚠️ OSTRZEŻENIE! ⚠️', 'background: red; color: yellow; font-size: 30px; padding: 10px; font-weight: bold;');
    console.log('%cKorzystanie z tej konsoli może umożliwić hakerom podszywanie się pod Ciebie oraz kradzież danych Twoich lub Twoich klientów poprzez atak zwany Self-XSS.', 'font-size: 20px;');
    console.log('%cNie wpisuj oraz nie wklejaj kodu, którego działania nie rozumiesz.', 'font-size: 16px; color: red;');

    this.$bus.$on('bookero-set-language', this.setLang);

    // Sync the cookie banner with the initial UI language
    document.body.setAttribute('data-lang', this.appLanguage);
    window.dispatchEvent(new CustomEvent('bookero-lang-changed', { detail: this.appLanguage }));
  },
  methods: {
    setLang (newLang) {
      if (
        (newLang === 'pl' || newLang === 'en') &&
        newLang !== this.appLanguage
      ) {
        this.appLanguage = newLang;
        this.$i18n.locale = newLang;
        document.body.setAttribute('data-lang', this.appLanguage);
        // Notify the cookie banner so it can re-render on the fly
        window.dispatchEvent(new CustomEvent('bookero-lang-changed', { detail: this.appLanguage }));
      }
    }
  },
  beforeDestroy () {
    this.$bus.$off('bookero-set-language', this.setLang);
  }
});



// WEBPACK FOOTER //
// ./src/main.js